// src/modules/Events/Events.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import "./Events.css";

import SocialHiveEvents from "../SocialHive/views/SocialHiveEvents/SocialHiveEvents";
import SHEventInfoPage from "../SocialHive/views/SHEventInfoPage/SHEventInfoPage";
import UserSearchInput from "../ProjectsWindow/components/ProjectAuthorization/components/UserSearchInput";

import Services from "../../services/Services";
import { GeneralContext } from "../../GeneralProvider";
import { useLanguage } from "../../LanguageProvider";
import SHEventCard from "../SocialHive/components/SHEventCard/SHEventCard";

// ---- helpers ----
function uniqByEventID(list) {
  if (!Array.isArray(list)) return [];

  const map = new Map();

  for (const item of list) {
    const ev = item?.event || item;
    const key = ev?.eventID || ev?.id;

    if (!key) {
      const randKey = Symbol("no-key");
      map.set(randKey, item);
      continue;
    }

    if (!map.has(key)) {
      map.set(key, item);
    }
  }

  return Array.from(map.values());
}

export default function Events() {
  const { getTranslation, language } = useLanguage();
  const { myChannels, userId, handleJoinChannel, getMyChannels } =
    useContext(GeneralContext);
  const token = useMemo(() => localStorage.getItem("token"), []);

  // UI state
  const [search, setSearch] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Data state
  const channels = Array.isArray(myChannels) ? myChannels : [];
  const [joinedEvents, setJoinedEvents] = useState([]);
  const [upcomingFromAllChannels, setUpcomingFromAllChannels] = useState([]);
  const [popularChannels, setPopularChannels] = useState([]);
  const [showAllJoined, setShowAllJoined] = useState(false);
  const [showAllCommunities, setShowAllCommunities] = useState(false);

  // Loading states (skeleton)
  const [loadingJoinedEvents, setLoadingJoinedEvents] = useState(false);
  const [loadingPopularChannels, setLoadingPopularChannels] = useState(false);
  const [loadingUpcomingEvents, setLoadingUpcomingEvents] = useState(false);
  const [loadingMyChannels, setLoadingMyChannels] = useState(false); // RIGHT SIDEBAR

  // ------- Filters (sidebar için user'ın kanalları) -------
  const filteredChannels = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    if (!q) return channels;
    return channels.filter((c) => {
      const name = (c.channelTitle || c.title || "").toLowerCase();
      const desc = (c.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [channels, search]);

  // My channels (right sidebar) load
  useEffect(() => {
    if (typeof getMyChannels !== "function") return;

    setLoadingMyChannels(true);

    const maybePromise = getMyChannels();

    if (maybePromise && typeof maybePromise.finally === "function") {
      // getMyChannels bir Promise döndürüyorsa gerçek completion'a göre kapat
      maybePromise.finally(() => setLoadingMyChannels(false));
    } 
  }, []);

  // ------- Date helpers -------
  function safeParseDate(input, fallbackTime) {
    if (!input && fallbackTime) input = fallbackTime;
    if (!input) return null;

    if (typeof input === "number" || /^\d+$/.test(String(input))) {
      const num = Number(input);
      const ms = String(num).length === 10 ? num * 1000 : num;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }

    if (typeof input === "string" && input.includes(" ") && !input.includes("T")) {
      const d = new Date(input.replace(" ", "T"));
      if (!isNaN(d.getTime())) return d;
    }

    const d = new Date(input);
    if (!isNaN(d.getTime())) return d;

    if (typeof input === "object" && input?.date && input?.time) {
      const d2 = new Date(`${input.date}T${input.time}`);
      return isNaN(d2.getTime()) ? null : d2;
    }
    return null;
  }

  function pickStartDate(ev) {
    return (
      ev?.eventStartDateTime || // global / popular service alanı
      ev?.startDateTime ||
      ev?.startDate ||
      ev?.start ||
      ev?.date ||
      ev?.startsAt ||
      (ev?.startDate && ev?.startTime && `${ev.startDate}T${ev.startTime}`) ||
      null
    );
  }

  function isPast(dateLike) {
    const d = safeParseDate(dateLike);
    if (!d) return false;
    return d.getTime() < Date.now();
  }

  const sortByStart = (a, b) => {
    const ta = safeParseDate(pickStartDate(a))?.getTime() ?? 0;
    const tb = safeParseDate(pickStartDate(b))?.getTime() ?? 0;
    return ta - tb;
  };

  // --- joinedEvents için: tarih formatını getTranslation ile üret ---
  const formatJoinedDate = React.useCallback(
    (isoLike) => {
      const d = safeParse(isoLike);
      if (!d) return "-";

      const weekdayKey = d
        .toLocaleDateString("en-US", { weekday: "long" })
        .toLowerCase(); // monday, tuesday...
      const monthKey = d
        .toLocaleDateString("en-US", { month: "long" })
        .toLowerCase(); // january, february...

      const weekday = getTranslation(weekdayKey) || weekdayKey;
      const month = getTranslation(monthKey) || monthKey;

      const day = String(d.getDate()).padStart(2, "0");
      const hour = String(d.getHours()).padStart(2, "0");
      const minute = String(d.getMinutes()).padStart(2, "0");

      // Örn: "Pazartesi, 05 Mayıs 14:30"
      return `${weekday}, ${day} ${month} ${hour}:${minute}`;
    },
    [getTranslation]
  );

  function getChannelInitial(name) {
    if (!name) return "?";
    const trimmed = String(name).trim();
    if (!trimmed) return "?";
    return trimmed[0].toUpperCase();
  }

  function ChannelAvatar({ title, logo, className }) {
    const [hasError, setHasError] = React.useState(false);

    const showLogo = logo && !hasError;
    const initial = getChannelInitial(title);

    return (
      <div className={`channel-avatar ${className || ""}`}>
        {showLogo ? (
          <img
            src={logo}
            alt={`${title || "channel"} logo`}
            onError={() => setHasError(true)}
          />
        ) : (
          <span className="channel-avatar-initial">{initial}</span>
        )}
      </div>
    );
  }

  // ------- Fetch: Katıldığım Etkinlikler -------
  const fetchUsersEvents = () => {
    if (!token || !userId) return;
    setLoadingJoinedEvents(true);
    Services.SocialHiveGetUsersEvents(token, userId)
      .then((res) => {
        if (res?.data?.status && Array.isArray(res?.data?.result)) {
          const raw = res.data.result;
          const unique = uniqByEventID(raw);
          setJoinedEvents(unique);
        } else {
          setJoinedEvents([]);
        }
      })
      .catch((err) => {
        console.warn("[Events] SocialHiveGetUsersEvents error:", err);
        setJoinedEvents([]);
      })
      .finally(() => {
        setLoadingJoinedEvents(false);
      });
  };

  useEffect(() => {
    fetchUsersEvents();
  }, [token, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------- Fetch: Popüler etkinlikler -> upcomingFromAllChannels -------
  const loadAllUpcomingGlobal = React.useCallback(() => {
    if (!token) return;
    setLoadingUpcomingEvents(true);
    Services.SocialHiveGetPopularChannelEvents(token)
      .then((res) => {
        const list = Array.isArray(res?.data?.result) ? res.data.result : [];
        const unique = uniqByEventID(list);
        const upcoming = unique
          .map((ev) => ({
            ...ev,
            _channel: {
              channelID: ev.channelID,
            },
          }))
          .sort(sortByStart);
        setUpcomingFromAllChannels(upcoming);
      })
      .catch((err) => {
        console.warn("[Events] SocialHiveGetPopularChannelEvents error:", err);
        setUpcomingFromAllChannels([]);
      })
      .finally(() => {
        setLoadingUpcomingEvents(false);
      });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAllUpcomingGlobal();
  }, [loadAllUpcomingGlobal]);

  // ------- Fetch: Popüler topluluklar (service) -------
  useEffect(() => {
    if (!token) return;
    setLoadingPopularChannels(true);
    Services.SocialHiveGetPopularChannels(token)
      .then((res) => {
        if (res?.data?.status && Array.isArray(res?.data?.result)) {
          setPopularChannels(res.data.result);
        } else {
          setPopularChannels([]);
        }
      })
      .catch((err) => {
        console.warn("[Events] SocialHiveGetPopularChannels error:", err);
        setPopularChannels([]);
      })
      .finally(() => {
        setLoadingPopularChannels(false);
      });
  }, [token]);

  function formatToLongDate(timestamp) {
    try {
      const d = new Date(timestamp * 1000);

      // Map language codes to locale codes
      const localeMap = {
        en: "en-US",
        tr: "tr-TR",
        es: "es-ES",
        de: "de-DE",
        it: "it-IT",
        fr: "fr-FR",
      };

      const locale = localeMap[language] || "en-US";

      const main = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(d);
      const weekday = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(d);
      const capWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
      return `${main}, ${capWeekday}`;
    } catch (e) {
      return formatToDayShortMonthYear(timestamp);
    }
  }

  const monthKeys = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];

  function formatToDayShortMonthYear(timestamp) {
    const date = new Date(timestamp * 1000);
    const day = date.getDate(); // 3
    const monthKey = monthKeys[date.getMonth()];
    const monthName = getTranslation(monthKey); // "October" veya "Ekim"
    const shortMonth = monthName?.slice(0, 3) || monthKey.slice(0, 3); // fallback
    const year = date.getFullYear(); // 2025
    return `${day} ${shortMonth} ${year}`;
  }

  // ------- SocialHive route switches -------
  if (selectedChannel && !selectedEvent) {
    return (
      <div className="events-host">
        <SocialHiveEvents
          activeChannel={selectedChannel}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          handleChannelChange={setSelectedChannel}
          showBackToEvents={true}
          onBackFromEvents={() => {
            setSelectedEvent(null);
            setSelectedChannel(null); // ana Events sayfasına dön
            fetchUsersEvents();
          }}
        />
      </div>
    );
  }

  if (selectedChannel && selectedEvent) {
    return (
      <div className="events-host">
        <SHEventInfoPage
          event={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          isAdmin={selectedChannel?.isAdmin === "1" ? "1" : "0"}
          channelTitle={selectedChannel?.channelTitle || selectedChannel?.title}
          channelLogo={selectedChannel?.channelLogo || selectedChannel?.logo}
          getEvents={() => {}}
          handleChannelChange={setSelectedChannel}
        />
      </div>
    );
  }

  // ------- Derived views -------
  const joinedToShow = showAllJoined ? joinedEvents : joinedEvents.slice(0, 3);
  const popularToShow = showAllCommunities
    ? popularChannels
    : popularChannels.slice(0, 3);

  // ------- Render -------
  return (
    <div className="events-page">
      <div className="events-grid">
        <div className="events-col-left">
          {/* Katıldığın Etkinlikler */}
          <section className="events-section">
            <div className="events-section-head">
              <h2 className="events-section-title">
                {getTranslation("joinedEvents") || "Katıldığın Etkinlikler"}
              </h2>
              {joinedEvents?.length > 3 && !loadingJoinedEvents && (
                <button
                  className="ghost-link"
                  onClick={() => setShowAllJoined((s) => !s)}
                >
                  {showAllJoined
                    ? getTranslation("seeLess") || "See Less"
                    : getTranslation("seeAll") || "See All"}
                </button>
              )}
            </div>

            {loadingJoinedEvents ? (
              <div className="joined-events-grid joined-events-grid--tight">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div className="joined-event-card" key={`joined-skeleton-${idx}`}>
                    <div className="joined-event-media">
                      <div className="event-skeleton event-skeleton-cover" />
                    </div>
                    <div className="joined-event-body">
                      <div className="event-skeleton event-skeleton-title" />
                      <div className="event-skeleton event-skeleton-text" />
                      <div className="event-skeleton event-skeleton-text event-skeleton-text-short" />
                    </div>
                  </div>
                ))}
              </div>
            ) : joinedEvents?.length ? (
              <div className="joined-events-grid joined-events-grid--tight">
                {joinedToShow.map((ev) => {
                  // participantsCount -> yoksa userCount / user_count kullan
                  const participantCount =
                    ev?.participantsCount ??
                    (ev?.userCount ? parseInt(ev.userCount, 10) : undefined) ??
                    (ev?.user_count ? parseInt(ev.user_count, 10) : undefined);

                  return (
                    <button
                      key={ev?.eventID || ev?.id}
                      className="joined-event-card"
                      onClick={() => {
                        const ch =
                          channels.find(
                            (c) =>
                              c.channelID === ev?.channelID || c.id === ev?.channelID
                          ) || { channelID: ev?.channelID };
                        setSelectedChannel(ch);
                        setSelectedEvent(ev);
                      }}
                    >
                      <div className="joined-event-media">
                        <img
                          src={ev?.bannerImage || ev?.cover || ev?.image}
                          alt={ev?.eventTitle || "event"}
                          onError={(e) => {
                            e.currentTarget.src =
                              "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='320' height='180'><rect width='100%' height='100%' fill='%23222'/></svg>";
                          }}
                        />
                        {!!ev?.badge && (
                          <span className="joined-event-badge">{ev.badge}</span>
                        )}
                      </div>
                      <div className="joined-event-body">
                        <div className="joined-event-title">
                          {ev?.eventTitle || ev?.title || "-"}
                        </div>
                        <div className="joined-event-meta">
                          {/* 1. satır: tarih */}
                          <div className="joined-event-date">
                            {formatJoinedDate(pickStartDate(ev))}
                          </div>

                          {/* 2. satır: katılımcı sayısı (userCount / participantsCount) */}
                          {participantCount ? (
                            <div className="joined-event-participants">
                              {participantCount}{" "}
                              {getTranslation("participant") || "Katılımcı"}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="events-empty">
                {getTranslation("noJoinedEvents") ||
                  "Katıldığın etkinlik bulunmuyor."}
              </div>
            )}
          </section>

          {/* Popüler Topluluklar — SocialHiveGetPopularChannels */}
          <section className="events-section">
            <div className="events-section-head">
              <h2 className="events-section-title">
                {getTranslation("popularCommunities") || "Popüler Topluluklar"}
              </h2>
              {popularChannels?.length > 3 && !loadingPopularChannels && (
                <button
                  className="ghost-link"
                  onClick={() => setShowAllCommunities((s) => !s)}
                >
                  {showAllCommunities
                    ? getTranslation("seeLess") || "See Less"
                    : getTranslation("seeAll") || "See All"}
                </button>
              )}
            </div>

            {loadingPopularChannels ? (
              <div className="communities-card-grid">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div className="community-card" key={`community-skeleton-${idx}`}>
                    <div className="community-card-header">
                      <div className="community-logo">
                        <div className="event-skeleton event-skeleton-avatar" />
                      </div>
                      <div className="event-skeleton event-skeleton-pill" />
                    </div>
                    <div className="community-card-body">
                      <div className="event-skeleton event-skeleton-title" />
                      <div className="event-skeleton event-skeleton-text" />
                      <div className="event-skeleton event-skeleton-text event-skeleton-text-short" />
                    </div>
                  </div>
                ))}
              </div>
            ) : popularToShow.length ? (
              <div className="communities-card-grid">
                {popularToShow.map((item) => {
                  const local = channels.find(
                    (c) =>
                      c.channelID === item.channelID || c.id === item.channelID
                  );

                  const isJoined = !!local;

                  const memberCount =
                    local?.memberCount ||
                    (item.userCount ? parseInt(item.userCount, 10) : undefined) ||
                    (item.user_count ? parseInt(item.user_count, 10) : undefined);

                  const channelObj = {
                    ...(local || {}),
                    channelID: item.channelID,
                    channelTitle:
                      local?.channelTitle ||
                      local?.title ||
                      item?.title ||
                      "Community",
                    title:
                      local?.title ||
                      local?.channelTitle ||
                      item?.title ||
                      "Community",
                    channelLogo: local?.channelLogo || local?.logo || item.logo,
                    logo: local?.logo || local?.channelLogo || item.logo,
                    description: item.description || local?.description || "",
                    memberCount,
                    isPublic: item.isPublic,
                  };

                  return (
                    <button
                      key={item.channelID}
                      className="community-card"
                      onClick={() => setSelectedChannel(channelObj)}
                    >
                      <div className="community-card-header">
                        <ChannelAvatar
                          className="community-logo"
                          title={
                            channelObj.channelTitle || channelObj.title || "channel"
                          }
                          logo={channelObj.channelLogo || channelObj.logo}
                        />

                        <button
                          type="button"
                          className={
                            "community-join" +
                            (isJoined ? " community-join--joined" : "")
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isJoined) return;
                            if (typeof handleJoinChannel === "function") {
                              handleJoinChannel(item.channelID);
                            }
                          }}
                        >
                          {isJoined
                            ? getTranslation("joined") || "Joined"
                            : getTranslation("joinCommunity") || "Topluluğa Katıl"}
                        </button>
                      </div>

                      <div className="community-card-body">
                        <div className="community-card-title">
                          {channelObj.channelTitle ||
                            channelObj.title ||
                            "Community"}
                        </div>
                        <div className="community-card-desc">
                          {channelObj.description ||
                            "Lorem Ipsum is simply dummy text of the printing and typesetting industry."}
                        </div>
                        <div className="community-card-meta">
                          {!!channelObj.memberCount && (
                            <span className="meta-badge">
                              {channelObj.memberCount}{" "}
                              {getTranslation("participant") || "Üye"}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="events-empty">
                {getTranslation("noPopularCommunities") ||
                  "Şu anda gösterilecek popüler topluluk bulunmuyor."}
              </div>
            )}
          </section>

          {/* En alttaki: Popüler etkinlikler — SHEventCard ile, alt alta */}
          <section className="events-section">
            <div className="events-section-head">
              <h2 className="events-section-title">
                {getTranslation("popularEvents") || "Popüler Etkinlikler"}
              </h2>
            </div>

            {loadingUpcomingEvents ? (
              <div className="upcoming-list-vertical">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div
                    className="event-skeleton-card"
                    key={`upcoming-skeleton-${idx}`}
                  >
                    <div className="event-skeleton event-skeleton-cover" />
                    <div className="event-skeleton event-skeleton-title" />
                    <div className="event-skeleton event-skeleton-text" />
                  </div>
                ))}
              </div>
            ) : upcomingFromAllChannels?.length ? (
              <div className="upcoming-list-vertical">
                {upcomingFromAllChannels.map((ev) => (
                  <SHEventCard
                    key={
                      (ev?._channel?.channelID || ev?.channelID || "c") +
                      "-" +
                      (ev?.eventID || ev?.id)
                    }
                    isOnline={ev?.isOnline}
                    imageSrc={ev?.bannerImage || ev?.cover || ev?.image}
                    title={ev?.eventTitle || ev?.title || "-"}
                    date={formatToLongDate(pickStartDate(ev))}
                    dateBadge={""}
                    location={ev?.locationAddress || "-"}
                    website={ev?.website || ""}
                    isFree={ev?.isFree}
                    isAdmin={"0"}
                    event={ev}
                    getEvents={loadAllUpcomingGlobal}
                    participants={ev?.participantsCount}
                    onClick={() => {
                      const fromMyChannels =
                        channels.find(
                          (c) =>
                            c.channelID === ev.channelID || c.id === ev.channelID
                        ) || null;

                      const ch =
                        ev?._channel && !fromMyChannels
                          ? ev._channel
                          : fromMyChannels || {
                              channelID: ev.channelID,
                            };

                      setSelectedChannel(ch);
                      setSelectedEvent(ev);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="events-empty">
                {getTranslation("noUpcomingEvents") ||
                  "Topluluklarında gösterilecek etkinlik bulunmuyor."}
              </div>
            )}
          </section>
        </div>

        {/* RIGHT: sidebar (her zamanki gibi user'ın kanalları) */}
        <aside className="events-col-right">
          <div className="sidebar-card">
            <div className="sidebar-head">
              <h3>{getTranslation("communities") || "Topluluklar"}</h3>
              <button className="ghost-link">
                {getTranslation("discover") || "Keşfet"}
              </button>
            </div>

            <div className="sidebar-list">
              {loadingMyChannels ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <div
                    key={`sidebar-skeleton-${idx}`}
                    className="sidebar-item sidebar-item-skeleton"
                  >
                    <div className="sidebar-avatar">
                      <div className="event-skeleton event-skeleton-avatar" />
                    </div>
                    <div className="sidebar-item-body">
                      <div className="event-skeleton event-skeleton-title" />
                      <div className="event-skeleton event-skeleton-text event-skeleton-text-short" />
                    </div>
                  </div>
                ))
              ) : filteredChannels.length ? (
                filteredChannels.map((ch) => (
                  <button
                    key={ch.channelID || ch.id}
                    className="sidebar-item"
                    onClick={() => setSelectedChannel(ch)}
                  >
                    <ChannelAvatar
                      className="sidebar-avatar"
                      title={ch.channelTitle || ch.title || "channel"}
                      logo={ch.channelLogo || ch.logo}
                    />
                    <div className="sidebar-item-body">
                      <div className="sidebar-item-title">
                        {ch.channelTitle || ch.title || "Community"}
                      </div>
                      {!!ch.memberCount && (
                        <div className="sidebar-item-meta">
                          {ch.memberCount} {getTranslation("members") || "Üye"}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="sidebar-empty">
                  {getTranslation("noCommunities") ||
                    "Henüz üye olduğun topluluk yok."}
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ---- utils ---- */
function formatDateText(isoLike) {
  const d = safeParse(isoLike);
  if (!d) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function safeParse(v) {
  if (!v) return null;
  if (typeof v === "number" || /^\d+$/.test(String(v))) {
    const num = Number(v);
    const ms = String(num).length === 10 ? num * 1000 : num;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "string" && v.includes(" ") && !v.includes("T")) {
    const d = new Date(v.replace(" ", "T"));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}